import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, useSubscription } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/i18n/Translator';

const ManageSubscription = () => {
  const { user } = useAuth();
  const { createCustomerPortal } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      // User is logged in, redirect to Stripe customer portal
      handlePortalRedirect();
    } else {
      // User not logged in, redirect to login with return URL
      sessionStorage.setItem('returnTo', '/manage-subscription');
      navigate('/login?returnTo=/manage-subscription');
    }
  }, [user, navigate]);

  const handlePortalRedirect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Return to dashboard billing section after managing subscription
      const returnUrl = `${window.location.origin}/chat/dashboard#billing`;
      const { portal_url } = await createCustomerPortal(returnUrl, 'sv');
      
      // Redirect to Stripe customer portal
      window.location.href = portal_url;
    } catch (err: any) {
      console.error('Failed to create customer portal session:', err);
      setError(err.message || 'Failed to access billing portal');
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    handlePortalRedirect();
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard#billing');
  };

  // Show loading state
  if (loading && user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="mx-auto mb-4 h-8 w-8" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {t('manageSubscription.redirecting.title')}
          </h1>
          <p className="text-gray-600">
            {t('manageSubscription.redirecting.description')}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
            <h1 className="text-xl font-semibold text-red-900 mb-2">
              {t('manageSubscription.error.title')}
            </h1>
            <p className="text-red-700 mb-4">{error}</p>
            <div className="space-x-3">
              <Button onClick={handleRetry} variant="outline">
                {t('manageSubscription.error.retry')}
              </Button>
              <Button onClick={handleGoToDashboard}>
                {t('manageSubscription.error.goToDashboard')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // This should not be reached, but just in case
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader className="mx-auto mb-4 h-8 w-8" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
};

export default ManageSubscription;